import React, { useState, useEffect, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import AiDraftEditor from './Editor'
import AiDraftSuccessScreen from './SuccessScreen'
import AiDraftErrorDisplay from './ErrorDisplay'
import { WorkflowState, WORKFLOW_CONSTANTS, isDraftActionDecided, ErrorType } from '../../../models/aiAnalysis/workflow'
import type { AiDraftWorkflowOrchestratorProps, EditedPayload } from './types'
import { Requests, ReactQuery } from '../../../api'

import { classifyAiActionError, shouldCloseOnDismiss } from '../../../utils/aiAnalysis/actionErrors'
import styles from './styles.module.scss'

const getStateAnnouncement = (state: WorkflowState): string => {
  switch (state) {
    case WorkflowState.FORWARD_DRAFT:
      return 'Review and edit the forward message, then approve or decline.'
    case WorkflowState.FORWARD_SENT:
      return 'Forward message sent successfully. Loading acknowledge message.'
    case WorkflowState.ACKNOWLEDGE_DRAFT:
      return 'Review and edit the acknowledge message, then approve or decline.'
    case WorkflowState.COMPLETE:
      return 'Both messages sent successfully. Page will refresh momentarily.'
    case WorkflowState.DECLINED:
      return 'Draft cancelled. Page will refresh momentarily.'
    default:
      return ''
  }
}

const AiDraftWorkflowOrchestrator: React.FC<AiDraftWorkflowOrchestratorProps> = ({
  forwardAction,
  acknowledgeAction,
  isOpen,
  onClose,
  onFatalDismiss,
  onWorkflowStateChange
}) => {
  const getInitialWorkflowState = (): WorkflowState | null => {
    const forwardDecided = isDraftActionDecided(forwardAction)
    const acknowledgeDecided = isDraftActionDecided(acknowledgeAction)

    if (forwardDecided && acknowledgeDecided) {
      return null
    } else if (forwardDecided) {
      return WorkflowState.ACKNOWLEDGE_DRAFT
    } else {
      return WorkflowState.FORWARD_DRAFT
    }
  }

  const initialState = getInitialWorkflowState()
  const [workflowState, setWorkflowState] = useState<WorkflowState | null>(initialState)
  const [forwardText, setForwardText] = useState<string>(forwardAction.action_payload.text ?? '')
  const [acknowledgeText, setAcknowledgeText] = useState<string>(acknowledgeAction.action_payload.text ?? '')
  const [retryCount, setRetryCount] = useState<number>(0)
  const [declineError, setDeclineError] = useState<unknown>(null)
  const [announcement, setAnnouncement] = useState<string>('')
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (workflowState) {
      setAnnouncement(getStateAnnouncement(workflowState))

      if (onWorkflowStateChange) {
        onWorkflowStateChange(workflowState)
      }

      if (containerRef.current) {
        containerRef.current.focus()
      }
    }
  }, [workflowState, onWorkflowStateChange])

  const approveForwardMutation = useMutation({
    mutationFn: async ({
      actionId,
      decision,
      text
    }: {
      actionId: string
      decision: 'approved' | 'edited'
      text?: string
    }) => {
      const request = Requests.updateAiAction({ actionId, decision, text })
      return await ReactQuery.patchResource(request, request.data)
    },
    onSuccess: () => {
      setRetryCount(0)
      setWorkflowState(WorkflowState.FORWARD_SENT)
    },
    onError: () => {
      setRetryCount((prev) => prev + 1)
    },
    useErrorBoundary: false
  })

  const handleForwardApprove = async (
    actionId: string,
    decision: 'approved' | 'edited',
    editedPayload: EditedPayload | null
  ) => {
    const text = decision === 'edited' ? editedPayload?.text ?? forwardText : undefined

    try {
      await approveForwardMutation.mutateAsync({ actionId, decision, text })
    } catch (error) {
      // Handled by mutation's onError callback
    }
  }

  const approveAcknowledgeMutation = useMutation({
    mutationFn: async ({
      actionId,
      decision,
      text
    }: {
      actionId: string
      decision: 'approved' | 'edited'
      text?: string
    }) => {
      const request = Requests.updateAiAction({ actionId, decision, text })
      return await ReactQuery.patchResource(request, request.data)
    },
    onSuccess: () => {
      setRetryCount(0)
      setWorkflowState(WorkflowState.COMPLETE)
    },
    onError: () => {
      setRetryCount((prev) => prev + 1)
    },
    useErrorBoundary: false
  })

  const handleAcknowledgeApprove = async (
    actionId: string,
    decision: 'approved' | 'edited',
    editedPayload: EditedPayload | null
  ) => {
    const text = decision === 'edited' ? editedPayload?.text : undefined

    try {
      await approveAcknowledgeMutation.mutateAsync({ actionId, decision, text })
    } catch (error) {
      // Handled by mutation's onError callback
    }
  }

  // Single instance used for all decline calls, including concurrent ones in handleDecline.
  // Errors are caught via try/catch and stored in declineError state — declineActionMutation.error
  // is never read, so concurrent calls clobbering hook state has no effect.
  const declineActionMutation = useMutation({
    mutationFn: async (actionId: string) => {
      const request = Requests.updateAiAction({ actionId, decision: 'declined' })
      return await ReactQuery.patchResource(request, request.data)
    },
    useErrorBoundary: false
  })

  const handleDecline = async () => {
    setDeclineError(null)
    try {
      if (workflowState === WorkflowState.FORWARD_DRAFT) {
        // Neither action decided yet — decline both
        await Promise.all([
          declineActionMutation.mutateAsync(forwardAction.id),
          declineActionMutation.mutateAsync(acknowledgeAction.id)
        ])
      } else {
        // Forward already approved and executed — only decline acknowledge
        await declineActionMutation.mutateAsync(acknowledgeAction.id)
      }

      setWorkflowState(WorkflowState.DECLINED)
    } catch (err) {
      setDeclineError(err)
      setRetryCount((prev) => prev + 1)
    }
  }

  const currentError =
    declineError ??
    (workflowState === WorkflowState.FORWARD_DRAFT ? approveForwardMutation.error : approveAcknowledgeMutation.error)

  const classifiedError = currentError ? classifyAiActionError(currentError, retryCount) : null

  const handleDismissError = async () => {
    if (classifiedError && shouldCloseOnDismiss(classifiedError.type)) {
      if (classifiedError.type === ErrorType.ALREADY_DECIDED) {
        onClose()
        window.location.reload()
      } else if (classifiedError.type === ErrorType.FATAL) {
        if (!declineError) {
          // Fatal error came from an approve — actions are still pending, attempt cleanup
          const declinePromises = []
          if (workflowState === WorkflowState.FORWARD_DRAFT) {
            declinePromises.push(
              declineActionMutation.mutateAsync(forwardAction.id).catch(() => {}),
              declineActionMutation.mutateAsync(acknowledgeAction.id).catch(() => {})
            )
          } else if (workflowState === WorkflowState.ACKNOWLEDGE_DRAFT) {
            declinePromises.push(declineActionMutation.mutateAsync(acknowledgeAction.id).catch(() => {}))
          }
          await Promise.all(declinePromises)
        }
        // Reload in all cases so the page reflects actual server state.
        // onFatalDismiss (if provided) sets a sessionStorage flag to suppress autoOpen on reload.
        ;(onFatalDismiss ?? onClose)()
        window.location.reload()
      }
    } else {
      setDeclineError(null)
      if (workflowState === WorkflowState.FORWARD_DRAFT) {
        approveForwardMutation.reset()
      } else {
        approveAcknowledgeMutation.reset()
      }
    }
  }

  useEffect(() => {
    if (workflowState === WorkflowState.FORWARD_SENT) {
      const timer = setTimeout(() => {
        setWorkflowState(WorkflowState.ACKNOWLEDGE_DRAFT)
      }, WORKFLOW_CONSTANTS.TRANSITION_DELAY_MS)

      return () => clearTimeout(timer)
    }
    return undefined
  }, [workflowState])

  useEffect(() => {
    if (workflowState === WorkflowState.COMPLETE || workflowState === WorkflowState.DECLINED) {
      const timer = setTimeout(() => {
        window.location.reload()
      }, WORKFLOW_CONSTANTS.TRANSITION_DELAY_MS)

      return () => clearTimeout(timer)
    }
    return undefined
  }, [workflowState])

  if (!isOpen || workflowState === null) {
    return null
  }

  switch (workflowState) {
    case WorkflowState.FORWARD_DRAFT:
      if (classifiedError) {
        return (
          <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="Error">
            <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
              {announcement}
            </div>
            <AiDraftErrorDisplay
              error={classifiedError.message}
              errorType={classifiedError.type}
              onDismiss={handleDismissError}
            />
          </div>
        )
      }

      return (
        <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="AI draft workflow">
          <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
            {announcement}
          </div>
          <AiDraftEditor
            key={`${forwardAction.id}-${forwardAction.action_type}`}
            action={forwardAction}
            onApprove={handleForwardApprove}
            onDecline={handleDecline}
            currentText={forwardText}
            onTextChange={setForwardText}
          />
        </div>
      )

    case WorkflowState.FORWARD_SENT:
      return (
        <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="Success confirmation">
          <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
            {announcement}
          </div>
          <AiDraftSuccessScreen
            message={WORKFLOW_CONSTANTS.SUCCESS_MESSAGE_FORWARD}
            subtitle={WORKFLOW_CONSTANTS.SUCCESS_SUBTITLE_FORWARD}
            isLoading
          />
        </div>
      )

    case WorkflowState.ACKNOWLEDGE_DRAFT:
      if (classifiedError) {
        return (
          <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="Error">
            <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
              {announcement}
            </div>
            <AiDraftErrorDisplay
              error={classifiedError.message}
              errorType={classifiedError.type}
              onDismiss={handleDismissError}
            />
          </div>
        )
      }

      return (
        <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="AI draft workflow">
          <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
            {announcement}
          </div>
          <AiDraftEditor
            key={`${acknowledgeAction.id}-${acknowledgeAction.action_type}`}
            action={acknowledgeAction}
            onApprove={handleAcknowledgeApprove}
            onDecline={handleDecline}
            currentText={acknowledgeText}
            onTextChange={setAcknowledgeText}
          />
        </div>
      )

    case WorkflowState.COMPLETE:
      return (
        <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="Success confirmation">
          <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
            {announcement}
          </div>
          <AiDraftSuccessScreen
            message={WORKFLOW_CONSTANTS.SUCCESS_MESSAGE_COMPLETE}
            subtitle={WORKFLOW_CONSTANTS.SUCCESS_SUBTITLE_COMPLETE}
            isLoading
          />
        </div>
      )

    case WorkflowState.DECLINED:
      return (
        <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="Draft cancelled">
          <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
            {announcement}
          </div>
          <AiDraftSuccessScreen
            message={WORKFLOW_CONSTANTS.DECLINED_MESSAGE}
            subtitle={WORKFLOW_CONSTANTS.DECLINED_SUBTITLE}
            type="declined"
          />
        </div>
      )

    default:
      return null
  }
}

export default AiDraftWorkflowOrchestrator
